/* eslint-disable react/no-array-index-key,@typescript-eslint/prefer-nullish-coalescing */

import type { ComponentProps } from 'react';
import React from 'react';
import type { ImageSourcePropType } from 'react-native';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { HorizontalScrollRail } from '../HorizontalScrollRail';
import { RewardCard } from '../RewardCard';
import { getRewardIllustration } from './RewardsRail.utils';
import { LoadingPlaceholder } from '../LoadingPlaceholder';
import { getFormattedExpirationDate, slugify } from '../../utils';
import { useResponsive } from '../../hooks';

// ────────────────────────────────────────────────────────────────────────────────

export const RewardsRail = (props: RewardsRailProps) => {
  const {
    isLoading,
    headerText,
    headerVariation,
    outerOffset,
    rewards,
    withoutHeaderBorder,
  } = props;

  if (isLoading)
    return (
      <RewardsRailLoading
        headerText={headerText}
        headerVariation={headerVariation}
        outerOffset={outerOffset}
        withoutHeaderBorder={withoutHeaderBorder}
        testID="rewards-rail.loading"
      />
    );

  if (!rewards?.length) return null;

  return <RewardsRailFulfilled {...props} />;
};

// ─── TEMPLATES ──────────────────────────────────────────────────────────────────

const RewardsRailLoading = (props: RewardsRailBaseProps) => {
  const loadingPlaceholders = Array.from({ length: 3 });

  const { match } = useResponsive();
  const railLoadingSpaceStyle = match([
    styles.railLoadingSpaceXS,
    styles.railLoadingSpaceSM,
  ]);

  return (
    <>
      <RewardsRailBase scrollEnabled={false} showNavControls={false} {...props}>
        {loadingPlaceholders.map((_, index) => (
          <RewardsRailItemLoading key={index} />
        ))}
      </RewardsRailBase>

      <View style={railLoadingSpaceStyle} />
    </>
  );
};

const RewardsRailFulfilled = (props: Omit<RewardsRailProps, 'isLoading'>) => {
  const {
    rewards,
    selectedRewardId,
    headerText,
    footerText,
    deadlinePrefixText,
    onPress,
    ...restProps
  } = props;

  const { minWidth } = useResponsive();

  return (
    <RewardsRailBase
      headerText={headerText}
      footerText={footerText}
      showNavControls={minWidth.tablet}
      {...restProps}
    >
      {rewards?.map((reward, index) => {
        const rewardId = reward.id;
        const isSelected = selectedRewardId === rewardId;
        const imageSource = getRewardIllustration(index);

        return (
          <RewardsRailItem
            key={rewardId}
            reward={reward}
            onPress={onPress}
            isSelected={isSelected}
            imageSource={imageSource}
            deadlineText={deadlinePrefixText}
          />
        );
      })}
    </RewardsRailBase>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const RewardsRailBase = (props: RewardsRailBaseProps) => {
  const {
    headerText,
    footerText,
    children,
    outerOffset = -theme.spacing.large,
    ...restProps
  } = props;

  const { minWidth } = useResponsive();

  return (
    <HorizontalScrollRail
      heading={headerText}
      footerText={footerText}
      showNavControls={minWidth.tablet}
      gap={theme.spacing.medium}
      outerOffset={outerOffset}
      {...restProps}
    >
      {children as readonly React.ReactNode[]}
    </HorizontalScrollRail>
  );
};

const RewardsRailItem = (props: RewardsRailItemProps) => {
  const { reward, imageSource, isSelected, deadlineText = '', onPress } = props;
  const { id, name, expirationDate, expiresAt } = reward;

  const deadline = getFormattedExpirationDate(
    expirationDate || expiresAt,
    deadlineText,
  );

  const rewardNameSlug = slugify(name);

  const rewardCardTestID = isSelected
    ? `reward-card-selected-${rewardNameSlug}`
    : `reward-card-${rewardNameSlug}`;

  return (
    <View style={styles.railItem}>
      <RewardCard
        testID={rewardCardTestID}
        id={id}
        description={name}
        onPress={onPress}
        imageSource={imageSource}
        isSelected={isSelected}
        deadline={deadline}
      />
    </View>
  );
};

const RewardsRailItemLoading = () => {
  return (
    <View style={styles.railItem}>
      <LoadingPlaceholder rowHeight={120} palette="cream" />
    </View>
  );
};

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  railLoadingSpaceXS: {
    height: 26,
  },
  railLoadingSpaceSM: {
    height: 34,
  },
  railItem: {
    width: 296,
    height: 120,
    marginVertical: theme.spacing.xsmall,
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type RewardsRailProps = {
  readonly rewards?: readonly Reward[];
  readonly selectedRewardId?: Reward['id'];
  readonly onPress?: (rewardId: Reward['id']) => void;
  readonly deadlinePrefixText?: string;
  readonly isLoading?: boolean;
} & Pick<RewardsRailBaseProps, 'headerText' | 'footerText'> &
  Pick<
    ComponentProps<typeof HorizontalScrollRail>,
    | 'initialScrollIndex'
    | 'headerVariation'
    | 'outerOffset'
    | 'withoutHeaderBorder'
  >;

type RewardsRailBaseProps = {
  readonly headerText: string;
  readonly footerText?: string;
} & Partial<ComponentProps<typeof HorizontalScrollRail>>;

type RewardsRailItemProps = {
  readonly reward: Reward;
  readonly isSelected?: boolean;
  readonly imageSource: ImageSourcePropType;
  readonly onPress?: (rewardId: Reward['id']) => void;
  readonly deadlineText?: string;
};

type Reward = {
  readonly id: string;
  readonly name: string;

  // aliases
  readonly expirationDate?: string | null;
  readonly expiresAt?: string | null;
};
